import React from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "May 18, 2026";

export default function TermsPage() {
    return (
        <div className="min-h-screen bg-[#0B1220] text-slate-100 px-6 py-16">
            <div className="max-w-3xl mx-auto" data-testid="terms-page">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Legal</p>
                <h1 className="font-serif text-4xl sm:text-5xl mb-2">Terms of Service</h1>
                <p className="text-slate-400 text-sm mb-10">Effective {EFFECTIVE_DATE}</p>

                <Section title="1. Who we are">
                    The Butler Room is a service offered by <strong>All Purpose Ventures LLC</strong>,
                    a Florida limited liability company doing business as “The Butler Room” (“Butler
                    Room,” “we,” “us,” or “our”). Our website is{" "}
                    <a href="https://thebutlerroom.com" className="text-[#D4AF37] underline">thebutlerroom.com</a>.
                    For any questions, contact us at{" "}
                    <a href="mailto:info@allpurposeventures.com" className="text-[#D4AF37] underline">info@allpurposeventures.com</a>.
                </Section>

                <Section title="2. What The Butler Room is">
                    The Butler Room is a <strong>concierge referral and matchmaking platform</strong>{" "}
                    serving homeowners and property owners primarily in Central Florida. We connect
                    clients (“Clients”) with independent, vetted service professionals
                    (“Contractors” or “Partners”) for premium residential cleaning services.
                    <br /><br />
                    <strong>The Butler Room is not the service provider.</strong> All work is performed by
                    independent Contractors who are solely responsible for their own services,
                    insurance, licensing, pricing, scheduling, and outcomes.
                </Section>

                <Section title="3. How it works">
                    <ol className="list-decimal pl-5 space-y-2 text-slate-300">
                        <li>You submit a cleaning task on the site. Our AI generates a transparent price estimate.</li>
                        <li>You approve the estimate and pay The Butler Room through our secure Stripe checkout.</li>
                        <li>We match the task with a suitable Cleaner in our network. Each job is exclusively assigned to one Cleaner.</li>
                        <li>The Cleaner performs the work. The Butler Room is the merchant of record for your payment.</li>
                        <li>After the work is accepted, we pay the Cleaner out (70% of the client-paid price; we retain 30% as our platform fee).</li>
                    </ol>
                </Section>

                <Section title="4. Fees">
                    Pricing for each task is set transparently in your dashboard before checkout. The Butler Room
                    does not charge clients additional booking, convenience, or platform fees beyond the quoted
                    project price. Cleaners receive 70% of the client-paid price; The Butler Room retains 30%
                    as its platform fee — never charged to you.
                </Section>

                <Section title="5. AI-generated quotes">
                    Quotes generated by our AI assistant are <strong>estimates only</strong>. They
                    are based on the description and photos you provide and are not a binding offer.
                    The final price is set by the Contractor after assessing the job. The Butler
                    Room makes no warranty that AI estimates will match real-world pricing.
                </Section>

                <Section title="6. Independent contractors — disclaimer of liability">
                    Contractors are independent third parties — not employees, agents, partners, or
                    representatives of The Butler Room or All Purpose Ventures LLC. We do not
                    supervise, direct, or control the manner or means of their work.
                    <br /><br />
                    To the fullest extent permitted by Florida law, The Butler Room is <strong>not
                    liable</strong> for any acts, omissions, damage, loss, injury, theft, delay,
                    workmanship defects, contract disputes, or property damage caused by or arising
                    from work performed by any Contractor. Your sole recourse for service-related
                    issues is against the Contractor who performed the work.
                    <br /><br />
                    We make commercially reasonable efforts to vet Contractors (review identity,
                    services offered, and signed agreement), but we make <strong>no warranty</strong>{" "}
                    regarding any Contractor’s skill, licensing status, insurance coverage, criminal
                    history, or fitness for any particular task.
                </Section>

                <Section title="7. Your responsibilities">
                    <ul className="list-disc pl-5 space-y-2 text-slate-300">
                        <li>Provide accurate task details, address, and contact information.</li>
                        <li>Be present (or authorize someone) to provide property access at the scheduled time.</li>
                        <li>Confirm any specialized requests (e.g., access codes, pets on premises, fragile items) before the scheduled cleaning.</li>
                        <li>Pay through the platform as agreed.</li>
                        <li>Resolve any service-quality disputes in good faith via The Butler Room&apos;s support channel first.</li>
                    </ul>
                </Section>

                <Section title="8. Cancellations & refunds">
                    You may cancel a booked task in your dashboard. If you cancel before a Contractor has
                    started the work, you receive a full refund. If work is in progress, refunds are
                    handled case-by-case based on the Contractor&apos;s reported time and materials. To
                    request a refund, email <a href="mailto:info@allpurposeventures.com" className="text-[#D4AF37] underline">info@allpurposeventures.com</a> within
                    30 days. Stripe-processed payments are refunded to the original card.
                </Section>

                <Section title="9. Accounts">
                    You must be at least 18 years old to create an account. You are responsible for
                    keeping your password confidential and for any activity under your account.
                    Notify us promptly at info@allpurposeventures.com if you suspect unauthorized
                    use. We may suspend or terminate accounts that violate these Terms, abuse the
                    service, or behave abusively toward our Contractors or staff.
                </Section>

                <Section title="10. Acceptable use">
                    You agree not to: (a) misuse the platform for fraud, harassment, or illegal
                    activity; (b) submit false or misleading task information; (c) attempt to
                    bypass the platform to avoid paying agreed amounts to Contractors; (d) scrape,
                    reverse-engineer, or copy our software, AI outputs, or content; or (e) use the
                    platform to solicit Cleaners away from our network for the purpose of
                    avoiding platform fees.
                </Section>

                <Section title="11. Intellectual property">
                    All content on the site — including the brand name, logo, design, copy, and AI
                    outputs — is owned by All Purpose Ventures LLC or its licensors and is
                    protected by U.S. and international intellectual-property laws. You receive a
                    limited, revocable, non-exclusive license to use the site for its intended
                    purpose.
                </Section>

                <Section title="12. Third-party services">
                    The site uses third-party services including Stripe (payments), Resend (email
                    delivery), Google Gemini (AI), Google Analytics, and Microsoft Clarity. Your
                    use of those features is also subject to their respective terms and privacy
                    policies. See our{" "}
                    <Link to="/privacy" className="text-[#D4AF37] underline">Privacy Policy</Link>{" "}
                    for details.
                </Section>

                <Section title="13. Disclaimers">
                    THE PLATFORM IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT
                    PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
                    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO
                    NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
                </Section>

                <Section title="14. Limitation of liability">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL ALL PURPOSE VENTURES
                    LLC, ITS OFFICERS, MEMBERS, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS OR
                    REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY. OUR TOTAL AGGREGATE LIABILITY
                    TO YOU FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE PLATFORM
                    SHALL NOT EXCEED <strong>USD $100</strong>.
                </Section>

                <Section title="15. Indemnification">
                    You agree to defend, indemnify, and hold harmless The Butler Room and All
                    Purpose Ventures LLC from any claims, damages, or expenses (including reasonable
                    attorneys’ fees) arising out of your use of the platform, your violation of
                    these Terms, or your dealings with any Contractor.
                </Section>

                <Section title="16. Governing law & disputes">
                    These Terms are governed by the laws of the State of Florida, without regard to
                    its conflict-of-law rules. Any dispute will be resolved in the state or federal
                    courts located in <strong>Orange County, Florida</strong>, and you consent to
                    that jurisdiction and venue. You agree to first attempt to resolve any dispute
                    informally by emailing info@allpurposeventures.com for at least 30 days before
                    filing any formal action.
                </Section>

                <Section title="17. Changes to these terms">
                    We may update these Terms from time to time. We will post the new effective
                    date at the top of this page. If changes are material, we will notify
                    registered users by email. Continued use of the platform after changes take
                    effect constitutes acceptance.
                </Section>

                <Section title="18. Contact">
                    All Purpose Ventures LLC<br />
                    DBA The Butler Room<br />
                    Central Florida<br />
                    Email:{" "}
                    <a href="mailto:info@allpurposeventures.com" className="text-[#D4AF37] underline">
                        info@allpurposeventures.com
                    </a>
                </Section>

                <div className="mt-12 pt-8 border-t border-white/10 text-sm text-slate-400">
                    <Link to="/" className="text-[#D4AF37] hover:underline" data-testid="terms-back-home">← Back to home</Link>
                    <span className="mx-3 text-slate-600">·</span>
                    <Link to="/privacy" className="text-[#D4AF37] hover:underline" data-testid="terms-to-privacy">Privacy Policy</Link>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-10">
            <h2 className="font-serif text-2xl mb-3 text-white">{title}</h2>
            <div className="text-slate-300 leading-relaxed text-[15px]">{children}</div>
        </section>
    );
}
